/**
 * Expense Card Component
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { Edit, Trash2, ChevronDown, ChevronUp, Calendar, Paperclip } from 'lucide-react';
import { CATEGORIES, getCategoryIcon, getStatusBadge } from './config';

const ExpenseCard = ({ expense, isExpanded, onToggle, onEdit, onDelete }) => {
  const categoryConfig = CATEGORIES[expense.category] || CATEGORIES.other;

  return (
    <div className={`bg-slate-800 border border-slate-700 rounded-xl overflow-hidden transition-all duration-200 ${
      isExpanded ? 'ring-1 ring-amber-500/50' : ''
    }`}>
      {/* Main Row */}
      <div className="p-4 cursor-pointer hover:bg-slate-750" onClick={onToggle}>
        <div className="flex items-center gap-4">
          {/* Icon */}
          <div className={`p-3 rounded-lg ${categoryConfig.bg}`}>
            {getCategoryIcon(expense.category)}
          </div>
          
          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-white truncate">{expense.description}</h3>
              {expense.is_tax_deductible && (
                <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">
                  Tax Deductible
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1 text-sm text-slate-400">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(expense.date).toLocaleDateString('en-GB', { 
                  day: 'numeric', 
                  month: 'short', 
                  year: 'numeric' 
                })}
              </span>
              <span>{categoryConfig.label}</span>
            </div>
          </div>
          
          {/* Status & Amount */}
          <div className="text-right">
            <p className="text-xl font-bold text-white">£{expense.amount?.toLocaleString()}</p>
            <div className="mt-1">
              {getStatusBadge(expense.reimbursement_status)}
            </div>
          </div>
          
          {/* Expand Icon */}
          <div className="text-slate-500">
            {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </div>
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 border-t border-slate-700">
          <div className="pt-4 space-y-4">
            {expense.notes && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Notes</p>
                <p className="text-sm text-slate-300">{expense.notes}</p>
              </div>
            )}
            
            {expense.document_id && (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Paperclip className="h-4 w-4" />
                <span>Receipt attached</span>
              </div>
            )}
            
            <div className="flex items-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(expense);
                }}
                className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700"
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(expense);
                }}
                className="border-red-500/50 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                data-testid="delete-expense-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseCard;
